import { useState } from 'react';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Trash2, Search, Receipt } from 'lucide-react';
import storage from '../utils/storage';
import auth from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';

export default function SalesPage() {
  const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n || 0);
  const user = auth.getCurrentUser();
  const [products] = useState(storage.getProductsForUser(user));
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [customer, setCustomer] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [discount, setDiscount] = useState(0);

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku.toLowerCase().includes(searchTerm.toLowerCase())
  ).filter(p => p.quantity > 0);

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      if (existingItem.quantity >= product.quantity) {
        toast.error('Not enough stock available');
        return;
      }
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} added to cart`);
  };

  const updateQuantity = (id, change) => {
    setCart(cart.map(item => {
      if (item.id === id) {
        const newQty = item.quantity + change;
        const product = products.find(p => p.id === id);
        if (newQty > product.quantity) {
          toast.error('Not enough stock available');
          return item;
        }
        if (newQty <= 0) return null;
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (id) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.sellingPrice * item.quantity), 0);
  const discountAmount = subtotal * (discount / 100);
  const total = subtotal - discountAmount;

  const completeSale = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    const transaction = {
      id: `txn-${Date.now()}`,
      date: new Date().toISOString(),
      products: cart.map(item => ({
        productId: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.sellingPrice,
        cost: item.costPrice
      })),
      subtotal,
      discount: discountAmount,
      total,
      totalCost: cart.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0),
      profit: total - cart.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0),
      paymentMethod,
      customer: customer || 'Walk-in Customer',
      user: user.username,
      userId: user.id
    };

    // Update product quantities (apply to global product list)
    const allProducts = storage.getProducts();
    const updatedProducts = allProducts.map(product => {
      const cartItem = cart.find(item => item.id === product.id);
      if (cartItem) {
        return { ...product, quantity: product.quantity - cartItem.quantity, lastUpdated: new Date().toISOString() };
      }
      return product;
    });
    storage.setProducts(updatedProducts);

    // Save transaction
    const transactions = storage.getTransactions();
    transactions.push(transaction);
    storage.setTransactions(transactions);

    // Update stock history
    const stockHistory = storage.getStockHistory();
    cart.forEach(item => {
      stockHistory.push({
        id: `hist-${Date.now()}-${item.id}`,
        productId: item.id,
        date: new Date().toISOString(),
        actionType: 'Sale',
        quantityChange: -item.quantity,
        reason: 'Sale',
        notes: `Transaction #${transaction.id.slice(-8)}`,
        user: user.username,
        userId: user.id
      });
    });
    storage.setStockHistory(stockHistory);

    toast.success('Sale completed successfully!');
    setCart([]);
    setCustomer('');
    setDiscount(0);
    setPaymentMethod('cash');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl md:text-5xl font-black font-heading tracking-tight">Sales / POS</h1>
        <p className="text-muted-foreground mt-2">Record sales and manage transactions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Products */}
        <div className="lg:col-span-8">
          <Card>
            <CardHeader>
              <CardTitle>Products</CardTitle>
              <CardDescription>Search and add products to cart</CardDescription>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  data-testid="search-sale-products"
                  placeholder="Search products..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto">
                {filteredProducts.map(product => (
                  <Card 
                    key={product.id} 
                    className="card-hover cursor-pointer"
                    onClick={() => addToCart(product)}
                    data-testid={`sale-product-${product.id}`}
                  >
                    <CardContent className="p-4">
                      {product.image && (
                        <img src={product.image} alt={product.name} className="w-full h-32 object-cover rounded-sm mb-2" />
                      )}
                      <h4 className="font-bold text-sm leading-tight mb-1">{product.name}</h4>
                      <p className="text-xs text-muted-foreground mb-2">Stock: {product.quantity}</p>
                      <p className="text-lg font-bold text-primary">{formatINR(product.sellingPrice)}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cart */}
        <div className="lg:col-span-4">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart size={20} />
                Cart ({cart.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-start gap-3 p-3 border rounded-sm">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm leading-tight">{item.name}</p>
                        <p className="text-xs text-muted-foreground">{formatINR(item.sellingPrice)} each</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateQuantity(item.id, -1)}>
                          <Minus size={14} />
                        </Button>
                        <span className="font-bold text-tabular w-8 text-center">{item.quantity}</span>
                        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => updateQuantity(item.id, 1)}>
                          <Plus size={14} />
                        </Button>
                        <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => removeFromCart(item.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {cart.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Cart is empty
                    </div>
                  )}
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Customer Name (Optional)</Label>
                  <Input
                    data-testid="customer-name-input"
                    value={customer}
                    onChange={(e) => setCustomer(e.target.value)}
                    placeholder="Enter customer name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger data-testid="payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Credit/Debit Card</SelectItem>
                      <SelectItem value="digital">Digital Wallet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Discount (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={discount}
                    onChange={(e) => setDiscount(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                    placeholder="0"
                  />
                </div>

                <Separator />

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-semibold text-tabular">{formatINR(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-success">
                      <span>Discount ({discount}%):</span>
                      <span className="font-semibold text-tabular">-{formatINR(discountAmount)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-primary text-tabular">{formatINR(total)}</span>
                  </div>
                </div>

                <Button 
                  className="w-full font-bold uppercase tracking-wide"
                  onClick={completeSale}
                  data-testid="complete-sale-btn"
                  disabled={cart.length === 0}
                >
                  <Receipt size={20} className="mr-2" />
                  Complete Sale
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}